import { Alert, AlertTitle, Box, Chip, Stack, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listActivePlanBlancs } from "@/api/planBlanc";
import { listOrganizations } from "@/api/organizations";

const SEVERITY_COLOR: Record<string, "info" | "warning" | "error"> = {
  ALPHA: "info",
  BRAVO: "warning",
  CHARLIE: "error",
};

/**
 * Global notice that surfaces above any page when a Plan Blanc is active for
 * one of the organisations the user can see. Polls every 60 s so it appears
 * within a minute of a declaration without a full page reload.
 */
export function PlanBlancBanner() {
  const { t } = useTranslation();

  const events = useQuery({
    queryKey: ["plan-blanc-active"],
    queryFn: listActivePlanBlancs,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  const rows = events.data ?? [];
  if (rows.length === 0) return null;

  const orgById = new Map(
    (orgs.data?.items ?? []).map((o) => [o.id, `${o.code} — ${o.name}`]),
  );

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {rows.map((e) => (
        <Alert
          key={e.id}
          severity={SEVERITY_COLOR[e.severity] ?? "warning"}
          icon={<WarningAmberIcon />}
          variant="filled"
          sx={{
            "& .MuiAlert-message": { width: "100%" },
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.85 },
            },
          }}
        >
          <AlertTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box component="span">
              {t("planBlanc.bannerTitle", { severity: e.severity })}
            </Box>
            <Chip
              size="small"
              variant="outlined"
              sx={{ color: "inherit", borderColor: "currentColor" }}
              label={orgById.get(e.organization_id) ?? e.organization_id.slice(0, 8)}
            />
          </AlertTitle>
          {e.reason && (
            <Typography variant="body2" component="div">
              {e.reason}
            </Typography>
          )}
          <Typography variant="caption" component="div" sx={{ opacity: 0.85, mt: 0.5 }}>
            {t("planBlanc.declaredAt", {
              date: new Date(e.declared_at).toLocaleString(),
            })}
          </Typography>
        </Alert>
      ))}
    </Stack>
  );
}
